import { useState, useEffect } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts'
import api from '../services/api'
import { useAuth } from '../contexts/AuthContext'

const CORES = ['#6366f1', '#10b981', '#f59e0b', '#8b5cf6', '#06b6d4', '#ec4899']

export default function Dashboard() {
    const { user, profile } = useAuth()
    const isMaster = profile?.user_role === 'master'

    const [deals, setDeals] = useState([])
    const [stages, setStages] = useState([])
    const [empresas, setEmpresas] = useState([])
    const [empresaSelecionada, setEmpresaSelecionada] = useState(null)
    const [mesSelecionado, setMesSelecionado] = useState('')

    useEffect(() => {
        if (isMaster) api.get('/companies').then(r => setEmpresas(r.data))
    }, [isMaster])

    useEffect(() => {
        const cId = isMaster ? empresaSelecionada : profile?.company_id
        if (!cId) {
            api.get('/stages').then(r => setStages(r.data))
        } else {
            api.get('/stages', { params: { company_id: cId } }).then(r => setStages(r.data))
        }
    }, [isMaster, empresaSelecionada, profile])

    useEffect(() => {
        if (!user) return
        const params = {}
        if (isMaster) {
            params.is_master = true
            if (empresaSelecionada) params.company_id = empresaSelecionada
        } else {
            params.is_master = false
            params.company_id = profile?.company_id
        }
        api.get('/deals', { params }).then(r => setDeals(r.data))
    }, [user, isMaster, empresaSelecionada, profile]) // eslint-disable-line

    // Filtro de mês
    const filtrarPorMes = (lista) => {
        if (!mesSelecionado) return lista
        return lista.filter(d => {
            const mes = new Date(d.created_at).toISOString().slice(0, 7)
            return mes === mesSelecionado
        })
    }

    const dealsFiltrados = filtrarPorMes(deals)
    const totalDeals = dealsFiltrados.length
    const openDeals = dealsFiltrados.filter(d => d.status === 'open')
    const wonDeals = dealsFiltrados.filter(d => d.status === 'won')
    const lostDeals = dealsFiltrados.filter(d => d.status === 'lost')
    const receitaTotal = wonDeals.reduce((s, d) => s + Number(d.value || 0), 0)
    const valorEmAberto = openDeals.reduce((s, d) => s + Number(d.value || 0), 0)
    const taxaConversao = totalDeals > 0 ? ((wonDeals.length / totalDeals) * 100).toFixed(1) : 0

    const dealsPorEtapa = stages.map(stage => ({
        name: stage.name,
        total: openDeals.filter(d => d.stage_id === stage.id).length,
    })).filter(s => s.total > 0)

    const produtosMap = {}
    dealsFiltrados.forEach(d => {
        if (!d.produto) return
        if (!produtosMap[d.produto]) produtosMap[d.produto] = 0
        produtosMap[d.produto]++
    })
    const dealsPorProduto = Object.entries(produtosMap).map(([name, value]) => ({ name, value }))

    const origensMap = {}
    dealsFiltrados.forEach(d => {
        if (!d.origem) return
        if (!origensMap[d.origem]) origensMap[d.origem] = 0
        origensMap[d.origem]++
    })
    const dealsPorOrigem = Object.entries(origensMap).map(([name, value]) => ({ name, value }))

    const mesesMap = {}
    wonDeals.forEach(d => {
        const mes = new Date(d.updated_at).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })
        if (!mesesMap[mes]) mesesMap[mes] = 0
        mesesMap[mes] += Number(d.value || 0)
    })
    const receitaPorMes = Object.entries(mesesMap).map(([mes, valor]) => ({ mes, valor }))

    const empresaAtual = empresas.find(e => e.id === empresaSelecionada)

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap justify-between items-center gap-3">
                <h1 className="text-2xl font-bold">Dashboard</h1>
                <div className="flex flex-wrap items-center gap-3">
                    {/* Filtro de mês */}
                    <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2">
                        <span className="text-xs text-gray-400">📅 Mês:</span>
                        <input
                            type="month"
                            value={mesSelecionado}
                            onChange={e => setMesSelecionado(e.target.value)}
                            className="bg-transparent text-sm outline-none text-white"
                        />
                        {mesSelecionado && (
                            <button onClick={() => setMesSelecionado('')} className="text-xs text-gray-500 hover:text-white transition ml-1">✕</button>
                        )}
                    </div>

                    {/* Filtro empresa (master) */}
                    {isMaster && (
                        <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-xl px-3 py-2">
                            <span className="text-xs text-gray-400">🏢</span>
                            <select
                                className="bg-transparent text-sm outline-none text-white"
                                value={empresaSelecionada || ''}
                                onChange={e => setEmpresaSelecionada(e.target.value || null)}
                            >
                                <option value="">Todas as empresas</option>
                                {empresas.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                            </select>
                            {empresaSelecionada && (
                                <button onClick={() => setEmpresaSelecionada(null)} className="text-xs text-gray-500 hover:text-white transition">✕</button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {mesSelecionado && (
                <div className="bg-indigo-900/20 border border-indigo-700/40 rounded-xl px-4 py-2 flex items-center gap-2">
                    <span className="text-indigo-400 text-sm">📅 Filtrando por: <span className="font-semibold">{new Date(mesSelecionado + '-01').toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}</span></span>
                    <button onClick={() => setMesSelecionado('')} className="text-xs text-indigo-400 hover:text-white ml-auto transition">✕ Limpar filtro</button>
                </div>
            )}

            {empresaAtual && (
                <p className="text-indigo-400 text-sm">🏢 Visualizando: {empresaAtual.name}</p>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                    <p className="text-gray-400 text-xs mb-1">Total de Negócios</p>
                    <p className="text-3xl font-bold text-white">{totalDeals}</p>
                    <p className="text-xs text-gray-500 mt-1">{openDeals.length} em aberto</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                    <p className="text-gray-400 text-xs mb-1">Receita Gerada</p>
                    <p className="text-3xl font-bold text-emerald-400">R$ {receitaTotal.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-500 mt-1">{wonDeals.length} negócios ganhos</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                    <p className="text-gray-400 text-xs mb-1">Em Negociação</p>
                    <p className="text-3xl font-bold text-indigo-400">R$ {valorEmAberto.toLocaleString('pt-BR')}</p>
                    <p className="text-xs text-gray-500 mt-1">{openDeals.length} deals ativos</p>
                </div>
                <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                    <p className="text-gray-400 text-xs mb-1">Taxa de Conversão</p>
                    <p className="text-3xl font-bold text-yellow-400">{taxaConversao}%</p>
                    <p className="text-xs text-gray-500 mt-1">{lostDeals.length} perdidos</p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {dealsPorEtapa.length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                        <h3 className="font-semibold mb-4 text-sm text-gray-300">Negócios por Etapa</h3>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={dealsPorEtapa}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
                                <Bar dataKey="total" fill="#6366f1" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {receitaPorMes.length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                        <h3 className="font-semibold mb-4 text-sm text-gray-300">Receita por Mês</h3>
                        <ResponsiveContainer width="100%" height={220}>
                            <LineChart data={receitaPorMes}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis dataKey="mes" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                                <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} formatter={(v) => [`R$ ${Number(v).toLocaleString('pt-BR')}`, 'Receita']} />
                                <Line type="monotone" dataKey="valor" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {dealsPorProduto.length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                        <h3 className="font-semibold mb-4 text-sm text-gray-300">Negócios por Produto</h3>
                        <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={dealsPorProduto} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                                    {dealsPorProduto.map((_, i) => <Cell key={i} fill={CORES[i % CORES.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {dealsPorOrigem.length > 0 && (
                    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                        <h3 className="font-semibold mb-4 text-sm text-gray-300">Origem dos Leads</h3>
                        <ResponsiveContainer width="100%" height={220}>
                            <BarChart data={dealsPorOrigem} layout="vertical">
                                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                                <XAxis type="number" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                                <YAxis dataKey="name" type="category" tick={{ fill: '#9ca3af', fontSize: 11 }} width={120} />
                                <Tooltip contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8 }} />
                                <Bar dataKey="value" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>

            {stages.length > 0 && (
                <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
                    <h3 className="font-semibold mb-4 text-sm text-gray-300">Funil de Conversão {mesSelecionado && <span className="text-indigo-400 text-xs ml-1">(mês filtrado)</span>}</h3>
                    <div className="space-y-3">
                        {stages.map((stage, index) => {
                            const totalNaEtapa = dealsFiltrados.filter(d => d.stage_id === stage.id).length
                            const totalGeral = dealsFiltrados.length
                            const percentual = totalGeral > 0 ? ((totalNaEtapa / totalGeral) * 100).toFixed(0) : 0
                            const proximaEtapa = stages[index + 1]
                            const totalProxima = proximaEtapa ? dealsFiltrados.filter(d => d.stage_id === proximaEtapa.id).length : null
                            const conversao = totalNaEtapa > 0 && totalProxima !== null ? ((totalProxima / totalNaEtapa) * 100).toFixed(0) : null
                            return (
                                <div key={stage.id}>
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="flex items-center gap-2">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }}></div>
                                            <span className="text-sm text-gray-300">{stage.name}</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-gray-500">{totalNaEtapa} leads</span>
                                            <span className="text-sm font-semibold text-white">{percentual}%</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-gray-700 rounded-full h-2 mb-1">
                                        <div className="h-2 rounded-full transition-all" style={{ width: `${percentual}%`, backgroundColor: stage.color }} />
                                    </div>
                                    {conversao !== null && totalNaEtapa > 0 && (
                                        <p className="text-xs text-gray-500 text-right mb-2">↓ {conversao}% avançaram para {proximaEtapa.name}</p>
                                    )}
                                </div>
                            )
                        })}
                        <div className="border-t border-gray-700 pt-3 mt-2 grid grid-cols-2 gap-4">
                            <div>
                                <div className="flex justify-between mb-1">
                                    <span className="text-sm text-emerald-400">✓ Ganhos</span>
                                    <span className="text-sm font-semibold text-white">{totalDeals > 0 ? ((wonDeals.length / totalDeals) * 100).toFixed(0) : 0}%</span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-2">
                                    <div className="h-2 rounded-full bg-emerald-500" style={{ width: `${totalDeals > 0 ? (wonDeals.length / totalDeals) * 100 : 0}%` }} />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{wonDeals.length} negócios fechados</p>
                            </div>
                            <div>
                                <div className="flex justify-between mb-1">
                                    <span className="text-sm text-red-400">✕ Perdidos</span>
                                    <span className="text-sm font-semibold text-white">{totalDeals > 0 ? ((lostDeals.length / totalDeals) * 100).toFixed(0) : 0}%</span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-2">
                                    <div className="h-2 rounded-full bg-red-500" style={{ width: `${totalDeals > 0 ? (lostDeals.length / totalDeals) * 100 : 0}%` }} />
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{lostDeals.length} negócios perdidos</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {totalDeals === 0 && (
                <div className="text-center py-16 text-gray-500">
                    <p className="text-4xl mb-3">📊</p>
                    <p>Nenhum dado para exibir {mesSelecionado ? 'neste mês' : 'ainda'}.</p>
                    <p className="text-sm mt-1">{mesSelecionado ? 'Tente selecionar outro mês ou limpe o filtro.' : 'Cadastre negócios no Pipeline para ver as métricas aqui!'}</p>
                </div>
            )}
        </div>
    )
}